import { useEffect } from 'react';

import { defineMessages, useIntl } from 'react-intl';

import AccountCircleIcon from '@/material-icons/400-24px/account_circle.svg?react';
import ChatBubbleIcon from '@/material-icons/400-24px/chat_bubble.svg?react';
import GroupsIcon from '@/material-icons/400-24px/groups.svg?react';
import StarActiveIcon from '@/material-icons/400-24px/star-fill.svg?react';
import StarIcon from '@/material-icons/400-24px/star.svg?react';
import type {
  ApiShortcutJSON,
  ApiShortcutTargetType,
} from 'mastodon/api_types/shortcuts';
import type { IconProp } from 'mastodon/components/icon';
import {
  getShortcutPath,
  getShortcutTitle,
} from 'mastodon/features/shortcuts/util';
import { ColumnLink } from 'mastodon/features/ui/components/column_link';
import { fetchShortcuts } from 'mastodon/reducers/slices/shortcuts';
import {
  selectShortcuts,
  selectShortcutsStatus,
} from 'mastodon/selectors/shortcuts';
import { useAppDispatch, useAppSelector } from 'mastodon/store';

import { CollapsiblePanel } from './collapsible_panel';

const messages = defineMessages({
  shortcuts: { id: 'navigation_bar.shortcuts', defaultMessage: 'Shortcuts' },
  expand: {
    id: 'navigation_panel.expand_shortcuts',
    defaultMessage: 'Expand shortcuts menu',
  },
  collapse: {
    id: 'navigation_panel.collapse_shortcuts',
    defaultMessage: 'Collapse shortcuts menu',
  },
});

const ICONS: Record<ApiShortcutTargetType, IconProp> = {
  account: AccountCircleIcon,
  group: GroupsIcon,
  chat_room: ChatBubbleIcon,
};

const ShortcutLink: React.FC<{ shortcut: ApiShortcutJSON }> = ({
  shortcut,
}) => {
  const to = getShortcutPath(shortcut);
  const title = getShortcutTitle(shortcut);

  if (!to || !title) {
    return null;
  }

  return (
    <ColumnLink
      icon=''
      iconComponent={ICONS[shortcut.target_type]}
      text={title}
      to={to}
      transparent
    />
  );
};

export const ShortcutsPanel: React.FC = () => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const shortcuts = useAppSelector(selectShortcuts);
  const status = useAppSelector(selectShortcutsStatus);

  useEffect(() => {
    void dispatch(fetchShortcuts());
  }, [dispatch]);

  return (
    <CollapsiblePanel
      to='/shortcuts'
      icon='star'
      iconComponent={StarIcon}
      activeIconComponent={StarActiveIcon}
      title={intl.formatMessage(messages.shortcuts)}
      collapseTitle={intl.formatMessage(messages.collapse)}
      expandTitle={intl.formatMessage(messages.expand)}
      loading={status === 'loading' && shortcuts.length === 0}
    >
      {shortcuts.map((shortcut) => (
        <ShortcutLink key={shortcut.id} shortcut={shortcut} />
      ))}
    </CollapsiblePanel>
  );
};
